class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;

    this.onDrawerClick = this.onDrawerClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeyDown =
      this.onDocumentKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  connectedCallback() {
    this.addEventListener(
      'click',
      this.onDrawerClick
    );

    document.addEventListener(
      'click',
      this.onDocumentClick
    );

    document.addEventListener(
      'keydown',
      this.onDocumentKeyDown
    );

    document.addEventListener(
      'keyup',
      this.onKeyUp
    );

    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    this.removeEventListener(
      'click',
      this.onDrawerClick
    );

    document.removeEventListener(
      'click',
      this.onDocumentClick
    );

    document.removeEventListener(
      'keydown',
      this.onDocumentKeyDown
    );

    document.removeEventListener(
      'keyup',
      this.onKeyUp
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!cartLink) return;

    cartLink.setAttribute(
      'role',
      'button'
    );

    cartLink.setAttribute(
      'aria-haspopup',
      'dialog'
    );

    cartLink.setAttribute(
      'aria-controls',
      'CartDrawer'
    );

    cartLink.setAttribute(
      'aria-expanded',
      this.classList.contains('active')
        ? 'true'
        : 'false'
    );
  }

  onDocumentClick(event) {
    const cartLink =
      event.target.closest(
        '#cart-icon-bubble'
      );

    if (!cartLink) return;

    event.preventDefault();
    this.open(cartLink);
  }

  onDocumentKeyDown(event) {
    const cartLink =
      event.target.closest(
        '#cart-icon-bubble'
      );

    if (
      !cartLink ||
      (
        event.code !== 'Space' &&
        event.code !== 'Enter'
      )
    ) {
      return;
    }

    event.preventDefault();
    this.open(cartLink);
  }

  onDrawerClick(event) {
    const closeTrigger =
      event.target.closest(
        '[data-cart-drawer-close]'
      );

    if (!closeTrigger) return;

    event.preventDefault();
    this.close();
  }

  onKeyUp(event) {
    if (
      event.code === 'Escape' &&
      this.classList.contains('active')
    ) {
      this.close();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.setAttribute(
      'aria-hidden',
      'false'
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add(
          'animate',
          'active'
        );

        document
          .querySelector(
            '#cart-icon-bubble'
          )
          ?.setAttribute(
            'aria-expanded',
            'true'
          );
      });
    });

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    const focusTarget =
      this.querySelector(
        '.drawer__close'
      ) || drawerInner;

    window.setTimeout(() => {
      if (
        drawerInner &&
        focusTarget &&
        typeof trapFocus === 'function'
      ) {
        trapFocus(
          drawerInner,
          focusTarget
        );
      } else {
        focusTarget?.focus();
      }
    }, 340);

    document.body.classList.add(
      'overflow-hidden'
    );
  }

  close() {
    this.classList.remove('active');

    this.setAttribute(
      'aria-hidden',
      'true'
    );

    document
      .querySelector(
        '#cart-icon-bubble'
      )
      ?.setAttribute(
        'aria-expanded',
        'false'
      );

    if (
      typeof removeTrapFocus ===
      'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    } else {
      this.activeElement?.focus();
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }

  /*
   * Required by Dawn product-form.js.
   *
   * When Add to Cart is submitted,
   * Shopify requests these two sections:
   * 1. Updated cart drawer
   * 2. Updated header cart icon/count
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    /*
     * Update the complete cart drawer
     * after Add to Cart.
     */
    const drawerSectionHTML =
      parsedState.sections?.[
        'cart-drawer'
      ];

    const currentDrawer =
      this.querySelector(
        '#CartDrawer'
      );

    const updatedDrawer =
      this.getSectionDOM(
        drawerSectionHTML,
        '#CartDrawer'
      );

    if (
      currentDrawer &&
      updatedDrawer
    ) {
      currentDrawer.innerHTML =
        updatedDrawer.innerHTML;
    }

    /*
     * Update the cart icon and count
     * in the header.
     */
    this.updateCartIcon(
      parsedState.sections?.[
        'cart-icon-bubble'
      ]
    );

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    const isEmpty =
      drawerInner?.dataset
        .cartEmpty === 'true';

    this.classList.toggle(
      'is-empty',
      isEmpty
    );

    this.setHeaderCartIconAccessibility();

    window.setTimeout(() => {
      this.open();
    }, 0);
  }

  updateCartIcon(sectionHTML) {
    if (!sectionHTML) return;

    const currentCartIcon =
      document.getElementById(
        'cart-icon-bubble'
      );

    if (!currentCartIcon) return;

    const updatedCartIcon =
      this.getSectionDOM(
        sectionHTML,
        '#cart-icon-bubble'
      );

    if (!updatedCartIcon) {
      console.warn(
        '[Cart drawer] Updated cart icon was not found.'
      );

      return;
    }

    currentCartIcon.innerHTML =
      updatedCartIcon.innerHTML;

    currentCartIcon.className =
      updatedCartIcon.className;

    const updatedHref =
      updatedCartIcon.getAttribute(
        'href'
      );

    if (updatedHref) {
      currentCartIcon.setAttribute(
        'href',
        updatedHref
      );
    }

    this.setHeaderCartIconAccessibility();
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const element =
      this.getSectionDOM(
        html,
        selector
      );

    return element
      ? element.innerHTML
      : null;
  }

  getSectionDOM(html, selector) {
    if (!html || !selector) {
      return null;
    }

    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}